/**
 * ADR-0155 §8.3 — the Microsoft 365 integration detail body (console Option A).
 *
 * Connect card: consent status (incl. the NAMED intermediate state
 * "Consented — awaiting site access"), the Get-consent-package gesture
 * (sendable unit with copy-to-clipboard), absolute timestamps.
 * Site access card: decision-pack link + Verify access (site URL → live probe)
 * — written for the weeks-later sitting: it states what's still missing.
 */

import { useState } from "react";
import { apiFetch } from "../../lib/api.js";
import { useToast } from "../Toast.js";
import { consentDisplay } from "../../lib/mirrorDisplay.js";

export interface MicrosoftDetailBlock {
  configured: boolean;
  consent_status: "pending" | "granted" | "revoked";
  consented_at: string | null;
  directory_domain: string | null;
  dependent_policy_count: number;
}

interface ConsentPackage {
  consent_url: string;
  decision_pack_url: string;
  expires_at: string;
  sendable_text: string;
}

export function MicrosoftIntegrationBody(props: {
  integrationId: string;
  microsoft: MicrosoftDetailBlock;
  lastTest: { at: string | null; status: "success" | "failed" | null; error: string | null };
  onReload: () => void;
}): JSX.Element {
  const toast = useToast();
  const { microsoft } = props;
  const state = consentDisplay(microsoft);

  const [pkg, setPkg] = useState<ConsentPackage | null>(null);
  const [pkgBusy, setPkgBusy] = useState(false);
  const [testBusy, setTestBusy] = useState(false);
  const [siteUrl, setSiteUrl] = useState("");
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [verifyResult, setVerifyResult] = useState<{ ok: boolean; message: string } | null>(null);

  async function getPackage() {
    setPkgBusy(true);
    try {
      const res = await apiFetch<ConsentPackage>(`/api/tenant/admin/integrations/${props.integrationId}/consent-package`);
      setPkg(res);
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setPkgBusy(false);
    }
  }

  async function copySendable() {
    if (!pkg) return;
    try {
      await navigator.clipboard.writeText(pkg.sendable_text);
      toast.push({ kind: "success", message: "Copied — paste it into an email to your Microsoft admin." });
    } catch {
      toast.push({ kind: "error", message: "Could not copy — select the text and copy manually." });
    }
  }

  async function testConnection() {
    setTestBusy(true);
    try {
      const res = await apiFetch<{ ok: boolean; message?: string }>(
        `/api/tenant/admin/integrations/${props.integrationId}/test`,
        { method: "POST", body: {} }
      );
      if (res.ok) toast.push({ kind: "success", message: "Microsoft connection is healthy." });
      else toast.push({ kind: "error", message: res.message ?? "Test failed." });
      props.onReload();
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setTestBusy(false);
    }
  }

  async function verifyAccess() {
    setVerifyBusy(true);
    setVerifyResult(null);
    try {
      const res = await apiFetch<{ ok: boolean; display?: string; message?: string }>(
        `/api/tenant/admin/integrations/${props.integrationId}/test`,
        { method: "POST", body: { site_url: siteUrl.trim() } }
      );
      setVerifyResult(
        res.ok
          ? { ok: true, message: `Atlas can write to ${res.display ?? "this site"}.` }
          : { ok: false, message: res.message ?? "Verification failed." }
      );
      props.onReload();
    } catch (e) {
      setVerifyResult({ ok: false, message: e instanceof Error ? e.message : String(e) });
    } finally {
      setVerifyBusy(false);
    }
  }

  return (
    <>
      <div className="card" style={{ display: "grid", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontWeight: 700 }}>Connection</div>
          <span className={state.pillClass}>{state.label}</span>
        </div>
        {state.hint ? <div className="muted" style={{ fontSize: 13 }}>{state.hint}</div> : null}
        <div className="muted" style={{ fontSize: 12 }}>
          {microsoft.directory_domain ? <>Directory: {microsoft.directory_domain} · </> : null}
          {microsoft.consented_at ? <>Consented {new Date(microsoft.consented_at).toLocaleString()} · </> : null}
          {props.lastTest.at ? (
            <>Last checked {new Date(props.lastTest.at).toLocaleString()}</>
          ) : (
            <>Never checked</>
          )}
        </div>
        {props.lastTest.status === "failed" && props.lastTest.error ? (
          <div className="help" style={{ color: "var(--danger-text)" }}>{props.lastTest.error}</div>
        ) : null}
        <div className="actions">
          <button type="button" className="btn btnPrimary" onClick={() => void getPackage()} disabled={pkgBusy || !microsoft.configured}>
            {pkgBusy ? "Preparing…" : "Get consent package"}
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => void testConnection()}
            disabled={testBusy || microsoft.consent_status !== "granted"}
          >
            {testBusy ? "Testing…" : "Test connection"}
          </button>
        </div>
        {pkg ? (
          <div style={{ display: "grid", gap: 8, borderTop: "1px solid var(--border)", paddingTop: 12 }}>
            <div className="muted" style={{ fontSize: 13 }}>
              Everything your Microsoft admin needs, ready to paste into an email. The consent link is valid until{" "}
              {new Date(pkg.expires_at).toLocaleString()}; requesting a new package invalidates this link.
            </div>
            <textarea className="input" readOnly rows={8} value={pkg.sendable_text} style={{ fontSize: 12 }} />
            <div className="actions">
              <button type="button" className="btn btnPrimary" onClick={() => void copySendable()}>
                Copy to clipboard
              </button>
              <a className="btn" href={pkg.decision_pack_url} target="_blank" rel="noreferrer">
                Preview the decision document
              </a>
            </div>
          </div>
        ) : null}
      </div>

      <div className="card" style={{ display: "grid", gap: 12 }}>
        <div style={{ fontWeight: 700 }}>Site access</div>
        <div className="muted" style={{ fontSize: 13 }}>
          After consent, Atlas can access no site until your admin grants one — the grant steps are in{" "}
          <a href="/api/integrations/microsoft/decision-pack" target="_blank" rel="noreferrer">
            the decision document
          </a>{" "}
          (section 6). There's no deadline; when they've granted a site, verify it here.
        </div>
        <label className="field">
          <span className="label">Site URL</span>
          <input
            className="input"
            value={siteUrl}
            onChange={(e) => setSiteUrl(e.target.value)}
            placeholder="https://yourcompany.sharepoint.com/sites/Deliverables"
            disabled={verifyBusy}
          />
        </label>
        <div className="actions">
          <button
            type="button"
            className="btn btnPrimary"
            onClick={() => void verifyAccess()}
            disabled={verifyBusy || siteUrl.trim() === "" || microsoft.consent_status !== "granted"}
            title={microsoft.consent_status !== "granted" ? "Admin consent comes first" : undefined}
          >
            {verifyBusy ? "Verifying…" : "Verify access"}
          </button>
          {verifyResult ? (
            verifyResult.ok ? <span className="pill pillOk">Access verified</span> : <span className="pill pillWarn">Not yet</span>
          ) : null}
        </div>
        {verifyResult ? <div className={verifyResult.ok ? "help" : "help"} style={verifyResult.ok ? undefined : { color: "var(--danger-text)" }}>{verifyResult.message}</div> : null}
      </div>
    </>
  );
}
